// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a Monday Madness text field that speaks a little Markdown. While it is being edited it
// is a plain textarea (Enter carries a list or to-do on); otherwise it shows the text the way
// the email will, and its to-dos can be ticked in place.

import {
	continueMondayList,
	type MondayInline,
	parseMondayMarkdown,
	toggleMondayCheck,
} from '@fluxer/schema/src/domains/konvo/MondayMarkdown';
import {useLingui} from '@lingui/react/macro';
import {IconCheck, IconPencil} from '@tabler/icons-react';
import {clsx} from 'clsx';
import type React from 'react';
import {useLayoutEffect, useMemo, useRef, useState} from 'react';
import styles from './MondayReportCard.module.css';

/** A textarea that grows with its text instead of scrolling. */
export function AutoTextarea({
	value,
	className,
	...rest
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & {value: string}) {
	const ref = useRef<HTMLTextAreaElement>(null);
	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;
		element.style.height = 'auto';
		element.style.height = `${element.scrollHeight}px`;
	}, [value]);
	return <textarea ref={ref} rows={1} value={value} className={clsx(styles.autoText, className)} {...rest} />;
}

function Inline({parts}: {parts: Array<MondayInline>}) {
	return parts.map((part, index) => {
		const key = `${index}:${part.text}`;
		if (part.href)
			return (
				<span key={key} className={styles.mdLink}>
					{part.text}
				</span>
			);
		if (part.bold) return <strong key={key}>{part.text}</strong>;
		if (part.italic) return <em key={key}>{part.text}</em>;
		if (part.strike) return <s key={key}>{part.text}</s>;
		return <span key={key}>{part.text}</span>;
	});
}

function MarkdownView({value, onToggle}: {value: string; onToggle: (line: number) => void}) {
	const blocks = useMemo(() => parseMondayMarkdown(value), [value]);
	return blocks.map((block, index) => {
		const key = `${index}:${block.kind}`;
		switch (block.kind) {
			case 'heading':
				return (
					<div key={key} className={styles.mdHeading} data-level={block.level}>
						<Inline parts={block.inline} />
					</div>
				);
			case 'check':
				return (
					<div key={key} className={styles.mdRow} style={{paddingLeft: block.depth * 20}}>
						<button
							type="button"
							role="checkbox"
							aria-checked={block.done}
							aria-label={block.inline.map((part) => part.text).join('')}
							className={styles.mdCheck}
							onClick={(event) => {
								event.stopPropagation();
								onToggle(block.line);
							}}
						>
							{block.done && <IconCheck size={11} stroke={3} aria-hidden="true" />}
						</button>
						<span className={clsx(block.done && styles.mdDone)}>
							<Inline parts={block.inline} />
						</span>
					</div>
				);
			case 'bullet':
				return (
					<div key={key} className={styles.mdRow} style={{paddingLeft: block.depth * 20}}>
						<span className={styles.mdBullet} aria-hidden="true" />
						<span>
							<Inline parts={block.inline} />
						</span>
					</div>
				);
			case 'number':
				return (
					<div key={key} className={styles.mdRow} style={{paddingLeft: block.depth * 20}}>
						<span className={styles.mdNumber}>{block.label}</span>
						<span>
							<Inline parts={block.inline} />
						</span>
					</div>
				);
			case 'paragraph':
				return (
					<p key={key} className={styles.mdParagraph}>
						{block.lines.map((line, lineIndex) => (
							<span key={lineIndex}>
								{lineIndex > 0 && <br />}
								<Inline parts={line} />
							</span>
						))}
					</p>
				);
			case 'gap':
				return <div key={key} className={styles.mdGap} />;
		}
	});
}

/** Enter at the end of a list line starts the next one; on an empty marker it ends the list. */
function carryList(
	event: React.KeyboardEvent<HTMLTextAreaElement>,
	onChange: (value: string) => void,
	maxLength: number,
) {
	if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
	const element = event.currentTarget;
	const {value, selectionStart, selectionEnd} = element;
	if (selectionStart !== selectionEnd) return;
	const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
	const newline = value.indexOf('\n', selectionStart);
	const lineEnd = newline === -1 ? value.length : newline;
	if (selectionStart !== lineEnd) return;
	const next = continueMondayList(value.slice(lineStart, lineEnd));
	if (!next) return;
	const text =
		next.kind === 'end'
			? value.slice(0, lineStart) + value.slice(lineEnd)
			: `${value.slice(0, selectionStart)}\n${next.prefix}${value.slice(selectionStart)}`;
	if (text.length > maxLength) return;
	event.preventDefault();
	const caret = next.kind === 'end' ? lineStart : selectionStart + 1 + next.prefix.length;
	// Write the DOM first: React then finds the same value and leaves the caret where it is put.
	element.value = text;
	element.setSelectionRange(caret, caret);
	onChange(text);
}

export function MondayMarkdownField({
	value,
	onChange,
	className,
	label,
	placeholder,
	maxLength,
}: {
	value: string;
	onChange: (value: string) => void;
	className: string;
	label: string;
	placeholder: string;
	maxLength: number;
}) {
	const {t} = useLingui();
	const [editing, setEditing] = useState(false);
	if (editing || !value.trim()) {
		return (
			<AutoTextarea
				className={className}
				aria-label={label}
				placeholder={placeholder}
				value={value}
				maxLength={maxLength}
				autoFocus={editing}
				onFocus={(event) => {
					setEditing(true);
					const end = event.currentTarget.value.length;
					event.currentTarget.setSelectionRange(end, end);
				}}
				onBlur={() => setEditing(false)}
				onKeyDown={(event) => carryList(event, onChange, maxLength)}
				onChange={(event) => onChange(event.target.value)}
			/>
		);
	}
	const edit = (event: React.SyntheticEvent<HTMLDivElement>) => {
		if (event.currentTarget.closest('fieldset')?.disabled) return;
		setEditing(true);
	};
	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: a click anywhere on the rendered text edits it; keyboards use the edit button
		// biome-ignore lint/a11y/useKeyWithClickEvents: the edit button below is the keyboard path
		<div className={clsx(className, styles.mdView)} data-flx="konvo-monday.markdown" onClick={edit}>
			<button
				type="button"
				className={styles.mdEdit}
				aria-label={t`Edit ${label}`}
				title={t`Edit ${label}`}
				onClick={(event) => {
					event.stopPropagation();
					setEditing(true);
				}}
			>
				<IconPencil size={14} stroke={1.8} aria-hidden="true" />
			</button>
			<MarkdownView value={value} onToggle={(line) => onChange(toggleMondayCheck(value, line))} />
		</div>
	);
}
